import {
  getImageSource,
  normalizeImageRefs as normalizeSharedImageRefs,
  selectPreferredImageUrl,
} from '@applauss/api-client';
import { getFutureOccurrences } from './ticketLinks';
import { getTodayKey } from './dateKey';

// The next showing from the viewer's day on, or the first occurrence if all are past.
// "Upcoming" is getFutureOccurrences — `local_date` against the viewer's day key, the
// rule the detail page's picker and title use — never `date_time`: that moment keeps
// last night's show for a viewer east of the venue after the picker has dropped it.
// Sorted by `local_date` then `local_time` (both sort as strings), like the API.
export const getNextOccurrence = (occurrences) => {
  if (!Array.isArray(occurrences) || occurrences.length === 0) return null;
  const slot = (occ) => `${occ.local_date || ''} ${occ.local_time || ''}`;
  const [next] = getFutureOccurrences(occurrences).sort((a, b) => slot(a).localeCompare(slot(b)));
  return next || occurrences[0];
};

// An event's next occurrence in either payload shape: the eager `occurrences` array, or
// the lazy one (>= 20 dated occurrences) that ships `next_occurrence` and no array.
// The API picks `next_occurrence` against the venue's day, so a viewer ahead of the
// venue can find it already behind them; trim to the viewer's day from
// `occurrence_dates`, as getNextOccurrence does for the array (#504). The stub carries
// no time: `next_occurrence`'s time belongs to the date it replaces.
export const getEventNextOccurrence = (event) => {
  const next = getNextOccurrence(event?.occurrences);
  if (next) return next;
  const lazyNext = event?.next_occurrence;
  if (!lazyNext) return null;
  const todayKey = getTodayKey();
  if ((lazyNext.local_date || '') >= todayKey) return lazyNext;
  const viewerNextDate = event.occurrence_dates?.find((date) => date >= todayKey);
  return viewerNextDate ? { local_date: viewerNextDate } : lazyNext;
};

/**
 * Select the best quality image from an array of images.
 * Prefer clean images, fall back to suspected images, and use blocked images
 * only when no other URL is available.
 * @param {Array} images - Array of image URLs (strings) or image objects
 * @param {Array} suspectedUrls - URLs awaiting image-review confirmation
 * @param {Array} blockedUrls - URLs confirmed as suspicious
 * @param {string} fallbackImage - Image to use when no candidate URLs exist
 * @returns {string} Best image URL or placeholder
 */
export const selectBestImage = (
  images,
  suspectedUrls = [],
  blockedUrls = [],
  fallbackImage = '/placeholder-event.jpg',
) => {
  if (!images || images.length === 0) {
    return fallbackImage;
  }

  const imageUrls = normalizeImageRefs(images).map(image => image.url);

  const suspectedSet = new Set(suspectedUrls);
  const blockedSet = new Set(blockedUrls);
  const cleanUrls = imageUrls.filter(url => !suspectedSet.has(url) && !blockedSet.has(url));
  const suspectedOnlyUrls = imageUrls.filter(url => suspectedSet.has(url) && !blockedSet.has(url));
  const candidateUrls = cleanUrls.length > 0
    ? cleanUrls
    : suspectedOnlyUrls.length > 0
      ? suspectedOnlyUrls
      : imageUrls;

  if (candidateUrls.length > 0) {
    return selectPreferredImageUrl(candidateUrls);
  }

  return images[0]?.url || images[0]?.src || images[0] || fallbackImage;
};
export const normalizeImageRefs = normalizeSharedImageRefs;

/**
 * Resolve the image selected by the API, with a legacy client-side fallback.
 * Public API responses should normally provide display_image; retaining the
 * fallback keeps older cached responses and local fixtures renderable.
 */
export const getEventDisplayImageRef = (event, fallbackImage = '/placeholder-event.jpg') => {
  const authoritativeImage = normalizeImageRefs([event?.display_image])[0];
  if (authoritativeImage) return authoritativeImage;

  const eventImages = normalizeImageRefs(event?.images);
  if (eventImages.length > 0) {
    const selectedUrl = selectBestImage(
      eventImages,
      event?.suspected_image_urls || [],
      event?.blocked_image_urls || [],
      fallbackImage,
    );
    return eventImages.find(image => image.url === selectedUrl)
      || { url: selectedUrl, source: getImageSource(selectedUrl, null) };
  }

  const legacyImage = normalizeImageRefs([event?.image])[0];
  return legacyImage || { url: fallbackImage, source: getImageSource(fallbackImage, null) };
};

export const getEventDisplayImageUrl = (event, fallbackImage = '/placeholder-event.jpg') => (
  getEventDisplayImageRef(event, fallbackImage).url
);

export const getEventMobileDisplayImageRef = (event, fallbackImage = '/placeholder-event.jpg') => (
  normalizeImageRefs([event?.mobile_display_image])[0]
  || getEventDisplayImageRef(event, fallbackImage)
);

export const getEventMobileDisplayImageUrl = (event, fallbackImage = '/placeholder-event.jpg') => (
  getEventMobileDisplayImageRef(event, fallbackImage).url
);
